import { useEffect, useMemo, useState } from "react";
import { Accordion, Badge, Placeholder } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import translate from "../../../../services/Translate";
import { completeNavigate } from "../../../../services/UtilityService";
import { SurveyAnswersByScope, SurveyEventItem, UserSurveyAnswersProps } from "./UserSurveyAnswers.d";
import { fetchUserSurveyAnswers } from "./UserSurveyAnswers.function";

const UserSurveyAnswers = ({ language, userId }: UserSurveyAnswersProps) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [surveyAnswers, setSurveyAnswers] = useState<SurveyAnswersByScope | null>(null);
  const [filterType, setFilterType] = useState("All");

  useEffect(() => {
    fetchUserSurveyAnswers(Number(userId), setSurveyAnswers, language, setLoading);
  }, [userId, language]);

  const _formatSurveyDate = (timestamp: number) => new Date(timestamp * 1000).toLocaleString();

  const _renderSurveyItem = (event: SurveyEventItem, index: number) => (
    <div
      key={index}
      onClick={(e) => completeNavigate(e, navigate, `/insights/surveys/answers/${event.user_id}/${event.timestamp}`)}
      className="d-flex justify-content-between align-items-center py-2 border-bottom clickable"
    >
      <div className="d-flex align-items-center flex-wrap gap-2">
        <strong>{event.survey_type}</strong>

        {event.sprint_id && (
          <span className="text-muted">
            ({translate(language, "PAGES.USER.SURVEY.SPRINT")} {event.sprint_id})
          </span>
        )}

        {event.meeting_id && (
          <span className="text-muted">
            ({translate(language, "PAGES.USER.SURVEY.MEETING")} {event.meeting_id})
          </span>
        )}

        {event.skipped && <Badge bg="secondary">{translate(language, "PAGES.USER.SURVEY.SKIPPED")}</Badge>}
      </div>

      <small className="text-muted">{_formatSurveyDate(event.timestamp)}</small>
    </div>
  );

  const scopes = useMemo(() => {
    return surveyAnswers ? ["All", ...Object.keys(surveyAnswers)] : ["All"];
  }, [surveyAnswers]);

  const isEmpty = useMemo(() => {
    return (
      !surveyAnswers || Object.values(surveyAnswers).every((section) => !section || Object.keys(section).length === 0)
    );
  }, [surveyAnswers]);

  if (loading) {
    return (
      <div className="p-md-5 p-3">
        <Placeholder as="h1" animation="glow" className="mb-4">
          <Placeholder xs={4} />
        </Placeholder>
        {Array(3)
          .fill(0)
          .map((_, index) => (
            <div key={index} className="border rounded p-3 mb-2">
              <Placeholder as="div" animation="glow" className="d-flex justify-content-between mb-2">
                <Placeholder xs={6} />
                <Placeholder xs={3} />
              </Placeholder>
              <Placeholder as="div" animation="glow" className="d-flex justify-content-between mb-2">
                <Placeholder xs={5} />
                <Placeholder xs={2} />
              </Placeholder>
            </div>
          ))}
      </div>
    );
  }

  return (
    <div className="p-md-5 p-3">
      <div className="mb-3 d-flex gap-2 flex-wrap">
        {scopes.map((scope) => (
          <Badge
            key={scope}
            bg={filterType === scope ? "primary" : "secondary"}
            className="clickable p-2"
            onClick={() => setFilterType(scope)}
          >
            {scope === "All" ? "All" : translate(language, `PAGES.USER.SURVEY.${scope.toUpperCase()}`)}
          </Badge>
        ))}
      </div>

      <h1 className="mb-4">{translate(language, "PAGES.USER.SURVEY.TITLE")}</h1>

      {isEmpty ? (
        <p className="text-muted">{translate(language, "PAGES.USER.SURVEY.NO_RESULTS")}</p>
      ) : (
        surveyAnswers &&
        Object.entries(surveyAnswers).map(([scopeKey, scopeValue]) => {
          if (filterType !== "All" && filterType !== scopeKey) return null;
          if (!scopeValue || Object.keys(scopeValue).length === 0) return null;

          return (
            <div key={scopeKey}>
              <h3 className="mb-3">{translate(language, `PAGES.USER.SURVEY.${scopeKey.toUpperCase()}`)}</h3>

              <Accordion alwaysOpen className="mb-5">
                {Object.entries(scopeValue).map(([groupKey, surveys], index) => (
                  <Accordion.Item key={groupKey} eventKey={`${scopeKey}-${index}`}>
                    <Accordion.Header>
                      {scopeKey === "global" ? groupKey : `${scopeKey.slice(0, -1)} ${groupKey}`}
                    </Accordion.Header>

                    <Accordion.Body>{(surveys as SurveyEventItem[]).map(_renderSurveyItem)}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
          );
        })
      )}
    </div>
  );
};

export default UserSurveyAnswers;
